import { Injectable } from '@angular/core'
import { HotToastService, ToastOptions } from '@ngxpert/hot-toast'

type Message = Parameters<HotToastService['success']>[0]

/**
 * HotToastService that shows a plain-text message only once while it is on screen.
 *
 * hot-toast skips a toast whose id is already showing, but every call gets a fresh id,
 * so three quick saves stacked three identical "User updated successfully!" toasts.
 * Keying the id on type + text collapses them into one. Toasts that pass their own id,
 * or show a component instead of text, are left alone. Loading toasts are not touched:
 * their refs are kept to update or close them.
 *
 * Provided in app.config.ts in place of HotToastService, so components inject it unchanged.
 */
@Injectable()
export class DedupedHotToastService extends HotToastService {
  override success<T> (message?: Message, options?: ToastOptions<T>) {
    return super.success(message, withTextId('success', message, options))
  }

  override error<T> (message?: Message, options?: ToastOptions<T>) {
    return super.error(message, withTextId('error', message, options))
  }

  override warning<T> (message?: Message, options?: ToastOptions<T>) {
    return super.warning(message, withTextId('warning', message, options))
  }

  override info<T> (message?: Message, options?: ToastOptions<T>) {
    return super.info(message, withTextId('info', message, options))
  }
}

function withTextId<T> (type: string, message: Message, options?: ToastOptions<T>): ToastOptions<T> | undefined {
  if (typeof message !== 'string' || options?.id) return options
  return { ...options, id: `${type}:${message}` }
}
